import { getNotificationTitle, type Notification } from '@maru44/huntre-utils/src/models/notification'
import { Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from '@mui/material'
import { useContext } from 'react'
import { Link } from 'react-router-dom'
import { Breads } from 'src/components/atoms/BreadCrumbs'
import { AuthContext } from 'src/components/providers/AuthProvider'
import { ClientContext } from 'src/components/providers/ClientProvider'
import { useNotifications } from 'src/hooks/notification/useNotifications'
import { formatTimestamp } from 'src/utils/firebase'
import { ErrorPage } from '../ErrorPage'

export const NotificationsPage = () => {
  const [user] = useContext(AuthContext)
  const { client } = useContext(ClientContext)

  const { listNotifications } = useNotifications(client?.id ?? '', user?.uid ?? '')

  const handleChangePage = (_: unknown, newPage: number) => {
    listNotifications.goToPage(newPage)
  }

  return (
    <>
      <Breads breadIds={['notifications']} />
      <Stack spacing={2}>
        <Typography variant="h5">通知一覧</Typography>
        {listNotifications.data && (
          <NotificationTable
            notifications={listNotifications.data}
            totalCount={listNotifications.totalCount}
            page={listNotifications.page}
            pageSize={listNotifications.pageSize}
            onPageChange={handleChangePage}
          />
        )}
        {listNotifications.error && <ErrorPage exception={listNotifications.error} />}
      </Stack>
    </>
  )
}

type TableProps = {
  notifications: Notification[]
  totalCount: number
  page: number
  pageSize: number
  onPageChange: (event: unknown, newPage: number) => void
}

export const NotificationTable = ({ notifications, totalCount, page, pageSize, onPageChange }: TableProps) => {
  return (
    <TableContainer component={Paper} elevation={6}>
      <Table sx={{ minWidth: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography variant="subtitle1">通知種別</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle1">要約</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="subtitle1">日時</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {notifications.map((n) => (
            <Row notification={n} key={n.id} />
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[pageSize]}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
      />
    </TableContainer>
  )
}

type RowProps = {
  notification: Notification
}

const Row = ({ notification }: RowProps) => {
  const { title, summary } = getNotificationTitle(notification)

  return (
    <TableRow>
      <TableCell component="th" scope="row">
        <Link to={`${notification.id}`}>
          <Typography sx={{ color: (theme) => (notification.readAt !== null ? theme.palette.action.active : theme.palette.secondary.main) }}>
            {title}
          </Typography>
        </Link>
      </TableCell>
      <TableCell>
        <Typography>{summary}</Typography>
      </TableCell>
      <TableCell>{formatTimestamp(notification.createdAt)}</TableCell>
    </TableRow>
  )
}
